// Use React from our shim to ensure consistent React instance
import { React, useState, useEffect, lazy, Suspense } from "./lib/react-shim";
// Import other dependencies
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { QueryClient } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthMiddleware from "@/middleware/AuthMiddleware";
import { VersionDisplay } from "@/components/ui/VersionDisplay";
import { supabase } from "@/integrations/supabase/client";

// Import pages
import Index from "@/pages/Index";
import Portal from "@/pages/portal/Portal";
import PortalAuth from "@/pages/portal/PortalAuth";
import PortalSignup from "@/pages/portal/PortalSignup";
import StreamlinedSignup from "@/pages/portal/StreamlinedSignup";
import PortalChatbots from "@/pages/portal/PortalChatbots";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalBillingPlan from "@/pages/portal/PortalBillingPlan";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalSettings from "@/pages/portal/PortalSettings";
import PortalBuilderWithContext from "@/pages/portal/PortalBuilderWithContext";
import PortalConversations from "@/pages/portal/PortalConversations";
import PortalConversationDetail from "@/pages/portal/PortalConversationDetail";
import FullscreenChat from "@/pages/FullscreenChat";
import UnifiedChatPage from "@/pages/UnifiedChatPage";
import NotFound from "@/pages/NotFound";
import BillingPage from "@/pages/settings/BillingPage";

// Import Admin components
import PortalLayout from '@/components/portal/PortalLayout';
import AdminPageLoader from '@/components/admin/AdminPageLoader';

// Import docs routes
import { docsRoutes } from '@/routes/docsRoutes';
// Import embedded routes
import { embeddedRoutes } from '@/routes/embeddedRoutes';

const queryClient = new QueryClient();

const LazyUserManagementPage = React.lazy(() => import('@/pages/admin/UserManagementPage'));
const LazyUserDetailsPage = React.lazy(() => import('@/pages/admin/UserDetailsPage'));
const LazyUserEditPage = React.lazy(() => import('@/pages/admin/UserEditPage'));
const LazyUserCreatePage = React.lazy(() => import('@/pages/admin/UserCreatePage'));
const LazyGlobalAISettingsPage = React.lazy(() => import('@/pages/admin/GlobalAISettingsPage'));
const LazyPlanManagementPage = React.lazy(() => import('@/pages/admin/PlanManagementPage'));
const LazyFieldsDictionaryPage = React.lazy(() => import('@/pages/admin/FieldsDictionaryPage'));

// Wrapper component to connect AuthMiddleware with AuthContext
function AuthWrapper() {
  const { updateAuthState } = useAuth();

  return (
    <AuthMiddleware onAuthStateChange={updateAuthState}>
      <div className="routes-container">
        <AppRoutes />
        <Toaster />
        <VersionDisplay position="bottom-right" detailed={true} />
      </div>
    </AuthMiddleware>
  );
}

function AppRoutes() {
  const { session, isLoading, isAdmin } = useAuth();

  // AuthMiddleware handles all authentication - remove session-based redirects

  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/portal/dashboard" element={<Navigate to="/portal" replace />} />
      <Route path="/portal/chatbots" element={<PortalChatbots />} />
      <Route path="/portal/billing" element={<Navigate to="/portal/billing/plan" />} />
      <Route path="/portal/billing/plan" element={<PortalBillingPlan />} />
      <Route path="/portal/billing/payment-methods" element={<PortalBilling />} />
      <Route path="/settings/billing" element={<BillingPage />} />
      <Route path="/portal/profile" element={<PortalProfile />} />
      <Route path="/portal/settings" element={<PortalSettings />} />
      <Route path="/portal/builder/:id?" element={<PortalBuilderWithContext />} />
      <Route path="/portal/conversations/:chatbotId" element={<PortalConversations />} />
      <Route path="/portal/conversation/:conversationId" element={<PortalConversationDetail />} />
      <Route path="/portal/conversation/:conversationId/:chatbotId" element={<PortalConversationDetail />} />

      <Route path="/chat/:chatId" element={<UnifiedChatPage />} />
      {/* Keep legacy route for backward compatibility */}
      <Route path="/fullscreen-chat/:chatId" element={<FullscreenChat />} />
      <Route path="/portal/auth" element={<PortalAuth />} />
      <Route path="/portal/signup" element={<PortalSignup />} />
      <Route path="/portal/signup-streamlined" element={<StreamlinedSignup />} />

      {/* === Embedded Chat Routes === */}
      {embeddedRoutes}

      {/* === Documentation Routes === */}
      {docsRoutes}

      {/* === Admin Routes (Integrated into Portal) === */}
      {/* Redirect /admin to /admin/users */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/users"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserManagementPage /></Suspense>}
      />
      <Route
        path="/admin/users/:userId"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserDetailsPage /></Suspense>}
      />
      <Route
        path="/admin/users/:userId/edit"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserEditPage /></Suspense>}
      />
      <Route
        path="/admin/users/new"
        element={<Suspense fallback={<AdminPageLoader />}><LazyUserCreatePage /></Suspense>}
      />
      <Route
        path="/admin/ai-settings"
        element={<Suspense fallback={<AdminPageLoader />}><LazyGlobalAISettingsPage /></Suspense>}
      />
      <Route
        path="/admin/plans"
        element={<Suspense fallback={<AdminPageLoader />}><LazyPlanManagementPage /></Suspense>}
      />
      <Route
        path="/admin/fields-dictionary"
        element={<Suspense fallback={<AdminPageLoader />}><LazyFieldsDictionaryPage /></Suspense>}
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  // Add a state to track React initialization status
  const [reactInitialized, setReactInitialized] = useState<boolean>(false);

  // Check if React is properly initialized
  useEffect(() => {
    if (React && React.forwardRef) {
      console.log('React is properly initialized:', React.version);
      setReactInitialized(true);
    } else {
      console.error('React forwardRef is not available!');
    }
  }, []);

  if (!reactInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app-container">
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <TooltipProvider>
              <AuthProvider>
                <AuthWrapper />
              </AuthProvider>
            </TooltipProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
